import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { InfoTooltip } from '@components/main-components/tooltip/InfoTooltip';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { DataTestId } from '@utils/test';
import { OwnerSellingNotAvailable, SellingNotAvailable } from '../TextInfo';
import {
  CannotSellGoldenEggInfo,
  MaxThresholdReachedInfo,
  NoEditionsToSellInfo,
} from '../TooltipTextInfo';

export const NoLiquidityContent = ({ hasNFT }: { hasNFT: boolean }) => {
  const { isAuthenticatedWithAPI } = useAuth();

  return !!hasNFT && !!isAuthenticatedWithAPI ? (
    <OwnerSellingNotAvailable />
  ) : (
    <SellingNotAvailable />
  );
};

export const MaxThresholdReached = ({ maxAmount }: { maxAmount: number }) => {
  const { tabletLandscape } = MediaQuery.useContainer();
  return (
    <ContentWrapper data-testid={DataTestId.BONDING_CURVE_MAX_THRESHOLD_REACHED}>
      Maximum of {maxAmount}
      {SPACE}
      {getSingularOrPlural({
        word: 'edition',
        count: maxAmount,
      })}
      {SPACE}
      can be sold.
      <InfoTooltip
        delayCloseMs={1000}
        openOnClick={!tabletLandscape}
        variant="outlined"
        size={16}
        portal
      >
        <MaxThresholdReachedInfo maxAmount={maxAmount} />
      </InfoTooltip>
    </ContentWrapper>
  );
};

export const UnableToSellErrorContent = ({
  onlyHasGoldenEgg = false,
}: {
  onlyHasGoldenEgg?: boolean;
}) => {
  const { tabletLandscape } = MediaQuery.useContainer();

  return (
    <ErrorContentWrapper
      data-testid={
        onlyHasGoldenEgg ? DataTestId.UNABLE_TO_SELL_GOLDEN_EGG : DataTestId.NO_EDITIONS_TO_SELL
      }
    >
      You can't sell any editions.
      <InfoTooltip
        delayCloseMs={1000}
        openOnClick={!tabletLandscape}
        variant="outlined"
        size={16}
        portal
      >
        {onlyHasGoldenEgg ? <CannotSellGoldenEggInfo /> : <NoEditionsToSellInfo />}
      </InfoTooltip>
    </ErrorContentWrapper>
  );
};

export const NotEnoughEditionsErrorContent = ({
  editionCount,
  onClick,
}: {
  editionCount: number;
  onClick: () => void;
}) => {
  return (
    <ErrorContentWrapper data-testid={DataTestId.USER_DOES_NOT_HAVE_ENOUGH_EDITIONS}>
      You can only sell {editionCount}{' '}
      {getSingularOrPlural({ word: 'edition', count: editionCount })}.
      <TextButton data-testid={DataTestId.REDUCE_QUANTITY_ERROR_BUTTON} onClick={onClick}>
        Reduce quantity
      </TextButton>
    </ErrorContentWrapper>
  );
};

export const AmountToSellContent = ({
  editionsOwned,
  maxLiquidationThreshold,
}: {
  editionsOwned: number;
  maxLiquidationThreshold: number;
}) => {
  const totalToSell =
    editionsOwned >= maxLiquidationThreshold ? maxLiquidationThreshold : editionsOwned;
  return (
    <ContentWrapper data-testid={DataTestId.AMOUNT_TO_SELL_CONTENT}>
      You have {totalToSell} to sell.
    </ContentWrapper>
  );
};

const ContentWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  paddingY: 10,
  base: 'M',
  fontWeight: 400,
  gap: 4,
});

const ErrorContentWrapper = styled(ContentWrapper, {
  color: '$destructive700',
});

const TextButton = styled('button', {
  base: 'M',
  color: '$destructive700',
  outline: 'none',
  textDecoration: 'underline',

  '&:hover': {
    cursor: 'pointer',
    opacity: 0.8,
  },
});
